/**
 * ScoreBandMigrationChart — Score band movement over time
 *
 * Shows member count per band across dates as a stacked area/line chart.
 * Falls back to DataSufficiencyWrapper if insufficient history.
 */
import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import type { BandMigrationPoint } from '../../../types/insightsTypes';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
);

const BAND_COLORS: Record<string, { border: string; bg: string }> = {
  '0-19':   { border: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' },    // red
  '20-39':  { border: '#f97316', bg: 'rgba(249, 115, 22, 0.15)' },   // orange
  '40-59':  { border: '#eab308', bg: 'rgba(234, 179, 8, 0.15)' },    // yellow
  '60-79':  { border: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)' },    // green
  '80-100': { border: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)' },   // blue
};

const BAND_LABELS: Record<string, string> = {
  '0-19': 'Critical (0–19)',
  '20-39': 'At Risk (20–39)',
  '40-59': 'Fair (40–59)',
  '60-79': 'Good (60–79)',
  '80-100': 'Excellent (80–100)',
};

interface ScoreBandMigrationChartProps {
  data: BandMigrationPoint[];
}

const ScoreBandMigrationChart: React.FC<ScoreBandMigrationChartProps> = ({ data }) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;

    // Get unique sorted periods (dates)
    const periods = [...new Set(data.map((d) => d.period))].sort();
    // Get unique sorted bands
    const bands = [...new Set(data.map((d) => d.band))].sort();

    // Build a lookup: period → band → count
    const lookup = new Map<string, Map<string, number>>();
    for (const d of data) {
      if (!lookup.has(d.period)) lookup.set(d.period, new Map());
      lookup.get(d.period)!.set(d.band, d.count);
    }

    // Format period labels
    const labels = periods.map((p) => {
      try {
        const d = new Date(p);
        return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      } catch {
        return p;
      }
    });

    const datasets = bands.map((band) => {
      const colors = BAND_COLORS[band] ?? { border: '#94a3b8', bg: 'rgba(148,163,184,0.15)' };
      return {
        label: BAND_LABELS[band] ?? band,
        data: periods.map((p) => lookup.get(p)?.get(band) ?? 0),
        borderColor: colors.border,
        backgroundColor: colors.bg,
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 2,
      };
    });

    return { labels, datasets };
  }, [data]);

  if (!chartData || chartData.labels.length < 2) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400 text-sm">
        Insufficient history for score band migration chart (minimum 2 periods required)
      </div>
    );
  }

  return (
    <Line
      data={chartData}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            position: 'bottom',
            labels: { boxWidth: 12, padding: 12, font: { size: 11 } },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y} member${ctx.parsed.y !== 1 ? 's' : ''}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { size: 11 } },
          },
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1,
              font: { size: 11 },
              callback: (v) => Number.isInteger(v) ? v : '',
            },
            grid: { color: 'rgba(0,0,0,0.05)' },
          },
        },
      }}
    />
  );
};

export default ScoreBandMigrationChart;
